<template>
  <fieldset class="c2c-prov">
    <legend>{{ t('provenance.legend') }} <span class="c2c-required">*</span></legend>

    <p class="c2c-prov-intro">{{ t('provenance.intro') }}</p>

    <!-- ROOT QUESTION: own vs third-party -->
    <div class="c2c-prov-options" role="radiogroup" :aria-label="t('provenance.legend')">
      <label class="c2c-prov-option" :class="{ 'c2c-prov-option--active': model.kind === 'own' }">
        <input
          type="radio"
          name="c2c-provenance-root"
          class="c2c-prov-radio-input"
          :checked="model.kind === 'own'"
          @change="setKind('own')"
        />
        <span class="c2c-prov-text">
          <strong>{{ t('provenance.own_root') }}</strong>
        </span>
      </label>

      <label class="c2c-prov-option" :class="{ 'c2c-prov-option--active': model.kind === 'third-party' }">
        <input
          type="radio"
          name="c2c-provenance-root"
          class="c2c-prov-radio-input"
          :checked="model.kind === 'third-party'"
          @change="setKind('third-party')"
        />
        <span class="c2c-prov-text">
          <strong>{{ t('provenance.third_root') }}</strong>
        </span>
      </label>
    </div>

    <p class="c2c-prov-undo-hint">{{ t('provenance.switch_hint') }}</p>

    <!-- ============================================================ -->
    <!--   BRANCH: created by me                                       -->
    <!-- ============================================================ -->
    <div v-if="model.kind === 'own'" class="c2c-prov-branch">

      <h3 class="c2c-prov-q">1. {{ t('provenance.own_q1') }}</h3>
      <div class="c2c-prov-radios" role="radiogroup" :aria-label="t('provenance.own_q1')">
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-own-ownership"
            :checked="model.ownership.kind === 'all-mine'"
            @change="setOwnership('all-mine')"
          />
          <span>{{ t('provenance.own_q1_a1') }}</span>
        </label>

        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-own-ownership"
            :checked="model.ownership.kind === 'contains-others'"
            @change="setOwnership('contains-others')"
          />
          <span>{{ t('provenance.own_q1_a2') }}</span>
        </label>

        <!-- Sub-question for "contains the work of others" -->
        <div v-if="model.ownership.kind === 'contains-others'" class="c2c-prov-sub">
          <h4>{{ t('provenance.own_q1_a2_sub') }}</h4>
          <div role="radiogroup" :aria-label="t('provenance.own_q1_a2_sub')">
            <label class="c2c-prov-radio">
              <input
                type="radio"
                name="c2c-own-others-status"
                :checked="model.ownership.kind === 'contains-others' && model.ownership.othersStatus === 'free-license'"
                @change="setOthersStatus('free-license')"
              />
              <span>{{ t('provenance.own_q1_a2_free') }}</span>
            </label>
            <label class="c2c-prov-radio">
              <input
                type="radio"
                name="c2c-own-others-status"
                :checked="model.ownership.kind === 'contains-others' && model.ownership.othersStatus === 'not-protected'"
                @change="setOthersStatus('not-protected')"
              />
              <span>{{ t('provenance.own_q1_a2_pd') }}</span>
            </label>
            <label class="c2c-prov-radio">
              <input
                type="radio"
                name="c2c-own-others-status"
                :checked="model.ownership.kind === 'contains-others' && model.ownership.othersStatus === 'copyright-protected'"
                @change="setOthersStatus('copyright-protected')"
              />
              <span>{{ t('provenance.own_q1_a2_protected') }}</span>
            </label>
            <label class="c2c-prov-radio">
              <input
                type="radio"
                name="c2c-own-others-status"
                :checked="model.ownership.kind === 'contains-others' && model.ownership.othersStatus === 'unknown'"
                @change="setOthersStatus('unknown')"
              />
              <span>{{ t('provenance.own_q1_a2_unknown') }}</span>
            </label>
          </div>

          <p
            v-if="model.ownership.kind === 'contains-others' &&
                  (model.ownership.othersStatus === 'copyright-protected' ||
                   model.ownership.othersStatus === 'unknown')"
            class="c2c-prov-block"
          >
            <AlertTriangle :size="16" />
            <span>{{
              model.ownership.kind === 'contains-others' && model.ownership.othersStatus === 'copyright-protected'
                ? t('provenance.block_protected')
                : t('provenance.block_unknown')
            }}</span>
          </p>
        </div>

        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-own-ownership"
            :checked="model.ownership.kind === 'ai-generated'"
            @change="setOwnership('ai-generated')"
          />
          <span>
            {{ t('provenance.own_q1_a3') }}
            <a href="https://commons.wikimedia.org/wiki/Commons:AI-generated_media" target="_blank" rel="noopener noreferrer">
              ({{ t('provenance.view_guidelines') }})
            </a>
          </span>
        </label>

        <!--
          Both fields are optional. Commons:AI-generated media encourages
          documenting the prompt, and names "engine, then the person who
          wrote the prompt" as best practice for the author credit; neither
          is a requirement, so neither blocks the upload.
        -->
        <div v-if="model.ownership.kind === 'ai-generated'" class="c2c-prov-sub">
          <label class="c2c-prov-sub-field">
            <span>{{ t('provenance.ai_tool_label') }}</span>
            <input
              type="text"
              maxlength="120"
              :placeholder="t('provenance.ai_tool_placeholder')"
              :value="aiTool"
              @input="setAiField('aiTool', ($event.target as HTMLInputElement).value)"
            />
          </label>
          <label class="c2c-prov-sub-field">
            <span>{{ t('provenance.ai_prompt_label') }}</span>
            <textarea
              rows="3"
              maxlength="1000"
              :placeholder="t('provenance.ai_prompt_placeholder')"
              :value="aiPrompt"
              @input="setAiField('aiPrompt', ($event.target as HTMLTextAreaElement).value)"
            ></textarea>
          </label>
          <p class="c2c-prov-sub-note">{{ t('provenance.ai_fields_note') }}</p>
        </div>
      </div>

      <h3 class="c2c-prov-q">2. {{ t('provenance.own_q2') }}</h3>
      <p class="c2c-prov-q-help">{{ t('provenance.own_q2_help') }}</p>
      <LicensePickerInline
        v-model="ownLicenseModel"
        :allowed="['cc-zero', 'cc-by-4.0', 'cc-by-sa-4.0']"
        :allow-custom="true"
        :allow-unknown="false"
      />
    </div>

    <!-- ============================================================ -->
    <!--   BRANCH: someone else                                        -->
    <!-- ============================================================ -->
    <div v-if="model.kind === 'third-party'" class="c2c-prov-branch">
      <p class="c2c-prov-intro">{{ t('provenance.third_intro') }}</p>

      <h3 class="c2c-prov-q">1. {{ t('provenance.third_q1') }}</h3>
      <p class="c2c-prov-q-help">{{ t('provenance.third_q1_help') }}</p>

      <div class="c2c-prov-radios" role="radiogroup" :aria-label="t('provenance.third_q1')">
        <!-- creator-released -->
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-third-reason"
            :checked="model.reason === 'creator-released'"
            @change="setReason('creator-released')"
          />
          <span v-html="tInterpolatedCcLink('provenance.third_a1')"></span>
        </label>
        <div v-if="model.reason === 'creator-released'" class="c2c-prov-sub">
          <h4>{{ t('provenance.third_a1_sub') }}</h4>
          <LicensePickerInline
            v-model="thirdLicenseModel"
            :allowed="['cc-zero', 'cc-by-4.0', 'cc-by-3.0', 'cc-by-2.5', 'cc-by-sa-4.0', 'cc-by-sa-3.0', 'cc-by-sa-2.5']"
            :allow-custom="true"
            :allow-unknown="true"
          />
        </div>

        <!-- heir -->
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-third-reason"
            :checked="model.reason === 'heir'"
            @change="setReason('heir')"
          />
          <span>{{ t('provenance.third_a2') }}</span>
        </label>
        <div v-if="model.reason === 'heir'" class="c2c-prov-sub">
          <h4>{{ t('provenance.third_a2_sub') }}</h4>
          <LicensePickerInline
            v-model="thirdLicenseModel"
            :allowed="['cc-by-4.0', 'cc-by-sa-4.0']"
            :allow-custom="false"
            :allow-unknown="false"
          />
        </div>

        <!-- not-protected -->
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-third-reason"
            :checked="model.reason === 'not-protected'"
            @change="setReason('not-protected')"
          />
          <span>{{ t('provenance.third_a3') }}</span>
        </label>
        <div v-if="model.reason === 'not-protected'" class="c2c-prov-sub c2c-prov-pd">
          <p class="c2c-prov-pd-eg">{{ t('provenance.third_a3_eg') }}
            <a href="https://commons.wikimedia.org/wiki/Commons:Licensing#Material_in_the_public_domain" target="_blank" rel="noopener noreferrer">{{ t('provenance.learn_more') }}</a>
          </p>
          <h4>{{ t('provenance.third_a3_sub') }}</h4>
          <p class="c2c-prov-pd-note">{{ t('provenance.third_a3_us_note') }}</p>
          <div class="c2c-prov-checks">
            <label class="c2c-prov-check">
              <input type="checkbox" :checked="hasPdReason('us-pre-1931')" @change="togglePdReason('us-pre-1931')" />
              <span>{{ t('provenance.pd_pre_1931') }}</span>
            </label>
            <label class="c2c-prov-check">
              <input type="checkbox" :checked="hasPdReason('author-deceased-70')" @change="togglePdReason('author-deceased-70')" />
              <span>{{ t('provenance.pd_deceased_70') }}</span>
            </label>
            <label class="c2c-prov-check">
              <input type="checkbox" :checked="hasPdReason('us-federal')" @change="togglePdReason('us-federal')" />
              <span>{{ t('provenance.pd_us_federal') }}</span>
            </label>
            <label class="c2c-prov-check">
              <input type="checkbox" :checked="hasPdReason('nasa')" @change="togglePdReason('nasa')" />
              <span>{{ t('provenance.pd_nasa') }}</span>
            </label>
            <label class="c2c-prov-check">
              <input type="checkbox" :checked="hasPdReason('unsure')" @change="togglePdReason('unsure')" />
              <span>{{ t('provenance.pd_unsure') }}</span>
            </label>
          </div>

          <div class="c2c-prov-field">
            <label :for="pdTagId">{{ t('provenance.pd_tag_label') }}</label>
            <input
              :id="pdTagId"
              type="text"
              :placeholder="t('provenance.pd_tag_placeholder')"
              :value="model.pdTag || ''"
              @input="onPdTagInput"
            />
            <small>{{ t('provenance.pd_tag_help') }}</small>
          </div>
        </div>

        <!-- permission -->
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-third-reason"
            :checked="model.reason === 'permission'"
            @change="setReason('permission')"
          />
          <span>{{ t('provenance.third_a4') }}</span>
        </label>
        <div v-if="model.reason === 'permission'" class="c2c-prov-sub">
          <p class="c2c-prov-vrt">
            <AlertTriangle :size="16" />
            <span v-html="tInterpolatedVrt('provenance.vrt_note')"></span>
          </p>
          <h4>{{ t('provenance.third_a4_sub') }}</h4>
          <LicensePickerInline
            v-model="thirdLicenseModel"
            :allowed="['cc-zero', 'cc-by-4.0', 'cc-by-sa-4.0']"
            :allow-custom="true"
            :allow-unknown="false"
          />
        </div>

        <!-- unknown -->
        <label class="c2c-prov-radio">
          <input
            type="radio"
            name="c2c-third-reason"
            :checked="model.reason === 'unknown'"
            @change="setReason('unknown')"
          />
          <span>{{ t('provenance.third_a5') }}</span>
        </label>
        <p v-if="model.reason === 'unknown'" class="c2c-prov-block">
          <AlertTriangle :size="16" />
          <span>{{ t('provenance.block_unknown') }}</span>
        </p>
      </div>

      <!-- Source URL (Q2) -->
      <h3 class="c2c-prov-q">2. {{ t('provenance.third_q2') }}</h3>
      <div class="c2c-prov-field">
        <input
          :id="urlId"
          type="url"
          inputmode="url"
          autocomplete="url"
          :placeholder="t('provenance.source_url_placeholder')"
          :value="model.sourceUrl"
          @input="onSourceUrlInput"
        />
        <small v-if="!sourceUrlValid" class="c2c-field-error">{{ t('provenance.source_url_invalid') }}</small>
      </div>

      <label class="c2c-prov-check">
        <input type="checkbox" :checked="model.isAiGenerated" @change="toggleAiGenerated" />
        <span>{{ t('provenance.third_ai_check') }}</span>
      </label>

      <!-- Original author (Q3) -->
      <h3 class="c2c-prov-q">3. {{ t('provenance.third_q3') }}</h3>
      <div class="c2c-prov-field">
        <input
          :id="authorId"
          type="text"
          autocomplete="off"
          :disabled="model.unknownAuthor"
          :placeholder="t('provenance.original_author_placeholder')"
          :value="model.originalAuthor"
          @input="onOriginalAuthorInput"
        />
      </div>
      <label class="c2c-prov-check">
        <input type="checkbox" :checked="model.unknownAuthor" @change="toggleUnknownAuthor" />
        <span>{{ t('provenance.third_unknown_author') }}</span>
      </label>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { AlertTriangle } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import LicensePickerInline from "./LicensePickerInline.vue"
import type {
  SourceProvenance, OwnProvenance, ThirdPartyProvenance,
  LicenseChoice, PdReason, OwnOwnership, ThirdPartyReason, LicenseId
} from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{ modelValue: SourceProvenance }>()
const emit = defineEmits<{ "update:modelValue": [SourceProvenance] }>()

const model = computed(() => props.modelValue)

const uid = Math.random().toString(36).slice(2, 8)
const urlId = "prov-url-" + uid
const authorId = "prov-author-" + uid
const pdTagId = "prov-pdtag-" + uid

/* --------- top-level kind ---------- */

function defaultLicense(): LicenseChoice {
  return { kind: "preset", id: "cc-by-sa-4.0" }
}

function setKind(kind: "own" | "third-party") {
  if (kind === "own") {
    if (model.value.kind === "own") return
    const next: OwnProvenance = {
      kind: "own",
      ownership: { kind: "all-mine" },
      license: defaultLicense()
    }
    emit("update:modelValue", next)
  } else {
    if (model.value.kind === "third-party") return
    const next: ThirdPartyProvenance = {
      kind: "third-party",
      reason: null,
      sourceUrl: "",
      originalAuthor: "",
      unknownAuthor: false,
      isAiGenerated: false,
      license: defaultLicense()
    }
    emit("update:modelValue", next)
  }
}

/* --------- own branch ---------- */

function setOwnership(kind: OwnOwnership["kind"]) {
  if (model.value.kind !== "own") return
  let ownership: OwnOwnership
  if (kind === "all-mine") ownership = { kind: "all-mine" }
  else if (kind === "ai-generated") ownership = { kind: "ai-generated" }
  else ownership = { kind: "contains-others", othersStatus: null }
  emit("update:modelValue", { ...model.value, ownership })
}

/** Empty strings while another ownership branch is active, so the template
 *  never has to narrow the union itself. */
const aiTool = computed(() =>
  model.value.kind === "own" && model.value.ownership.kind === "ai-generated"
    ? model.value.ownership.aiTool ?? "" : "")
const aiPrompt = computed(() =>
  model.value.kind === "own" && model.value.ownership.kind === "ai-generated"
    ? model.value.ownership.aiPrompt ?? "" : "")

function setAiField(field: "aiTool" | "aiPrompt", value: string) {
  if (model.value.kind !== "own" || model.value.ownership.kind !== "ai-generated") return
  emit("update:modelValue", {
    ...model.value,
    ownership: { ...model.value.ownership, [field]: value }
  })
}

function setOthersStatus(status: "free-license" | "not-protected" | "copyright-protected" | "unknown") {
  if (model.value.kind !== "own" || model.value.ownership.kind !== "contains-others") return
  emit("update:modelValue", {
    ...model.value,
    ownership: { kind: "contains-others", othersStatus: status }
  })
}

const ownLicenseModel = computed<LicenseChoice>({
  get: () => model.value.kind === "own" ? model.value.license : defaultLicense(),
  set: (val) => {
    if (model.value.kind !== "own") return
    emit("update:modelValue", { ...model.value, license: val })
  }
})

/* --------- third-party branch ---------- */

/**
 * Allowed preset license IDs for each third-party reason. Mirrors
 * UploadWizard's narrower sets (heir is BY/BY-SA only, permission is
 * the standard recommended trio). Keep in sync with the :allowed prop
 * each LicensePickerInline gets in the template above.
 */
const allowedPresetsByReason: Record<string, LicenseId[]> = {
  "creator-released": [
    "cc-zero", "cc-by-4.0", "cc-by-3.0", "cc-by-2.5",
    "cc-by-sa-4.0", "cc-by-sa-3.0", "cc-by-sa-2.5"
  ],
  "heir": ["cc-by-4.0", "cc-by-sa-4.0"],
  "permission": ["cc-zero", "cc-by-4.0", "cc-by-sa-4.0"]
}

function customAllowedFor(reason: string): boolean {
  // heir is the only third-party reason where UploadWizard does NOT let
  // the user enter an arbitrary license template - heirs can only
  // re-license under standard CC4 attribution variants.
  return reason !== "heir"
}

function unknownAllowedFor(reason: string): boolean {
  // "I don't know" only makes sense for creator-released. Heirs and
  // permission-holders must commit to a specific license.
  return reason === "creator-released"
}

/**
 * If the current license isn't valid for the new branch, snap it to
 * the first allowed preset rather than leaving the picker showing no
 * selection (which would let the user submit and silently pick up an
 * invalid template).
 */
function clampLicenseForReason(
  current: LicenseChoice | undefined,
  reason: string
): LicenseChoice {
  const allowed = allowedPresetsByReason[reason] || []
  const fallback: LicenseChoice = { kind: "preset", id: allowed[0] || "cc-by-sa-4.0" }
  if (!current) return fallback
  if (current.kind === "preset" && allowed.includes(current.id)) return current
  if (current.kind === "custom" && customAllowedFor(reason)) return current
  if (current.kind === "unknown" && unknownAllowedFor(reason)) return current
  return fallback
}

function setReason(reason: Exclude<ThirdPartyReason, null>) {
  if (model.value.kind !== "third-party") return
  // Reset branch-specific data when switching reason so we never carry
  // stale PD checkboxes into a license-based branch or vice versa.
  // License is clamped to a value that's valid in the new branch.
  const license = ["creator-released", "heir", "permission"].includes(reason)
    ? clampLicenseForReason(model.value.license, reason)
    : undefined
  const next: ThirdPartyProvenance = {
    ...model.value,
    reason,
    pdReasons: reason === "not-protected" ? (model.value.pdReasons || []) : undefined,
    pdTag: reason === "not-protected" ? (model.value.pdTag || "") : undefined,
    license
  }
  emit("update:modelValue", next)
}

const thirdLicenseModel = computed<LicenseChoice>({
  get: () => (model.value.kind === "third-party" ? (model.value.license || defaultLicense()) : defaultLicense()),
  set: (val) => {
    if (model.value.kind !== "third-party") return
    emit("update:modelValue", { ...model.value, license: val })
  }
})

function hasPdReason(r: PdReason): boolean {
  if (model.value.kind !== "third-party") return false
  return (model.value.pdReasons || []).includes(r)
}

function togglePdReason(r: PdReason) {
  if (model.value.kind !== "third-party" || model.value.reason !== "not-protected") return
  const cur = model.value.pdReasons || []
  const next = cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r]
  emit("update:modelValue", { ...model.value, pdReasons: next })
}

function onPdTagInput(e: Event) {
  if (model.value.kind !== "third-party") return
  emit("update:modelValue", { ...model.value, pdTag: (e.target as HTMLInputElement).value })
}

function onSourceUrlInput(e: Event) {
  if (model.value.kind !== "third-party") return
  emit("update:modelValue", { ...model.value, sourceUrl: (e.target as HTMLInputElement).value })
}

function onOriginalAuthorInput(e: Event) {
  if (model.value.kind !== "third-party") return
  emit("update:modelValue", { ...model.value, originalAuthor: (e.target as HTMLInputElement).value })
}

function toggleAiGenerated() {
  if (model.value.kind !== "third-party") return
  emit("update:modelValue", { ...model.value, isAiGenerated: !model.value.isAiGenerated })
}

function toggleUnknownAuthor() {
  if (model.value.kind !== "third-party") return
  const next = !model.value.unknownAuthor
  emit("update:modelValue", {
    ...model.value,
    unknownAuthor: next,
    originalAuthor: next ? "" : model.value.originalAuthor
  })
}

const sourceUrlValid = computed(() => {
  if (model.value.kind !== "third-party") return true
  const url = model.value.sourceUrl.trim()
  if (!url) return true
  try {
    const u = new URL(url)
    return u.protocol === "http:" || u.protocol === "https:"
  } catch { return false }
})

/* --------- localized strings with embedded links ---------- */

function tInterpolatedCcLink(key: string): string {
  // Embeds a Creative Commons link into the localized string at the
  // {cc_link} placeholder position. Used because v-html plus link target
  // is more reliable than a slot-based <i18n-t> here.
  const raw = t(key)
  return raw.replace(/\{cc_link\}/g,
    '<a href="https://creativecommons.org/share-your-work/cclicenses/" ' +
    'target="_blank" rel="noopener noreferrer">' + t('provenance.cc_link_text') + '</a>')
}

function tInterpolatedVrt(key: string): string {
  const raw = t(key)
  return raw
    .replace(/\{vrt_email\}/g,
      '<a href="mailto:permissions-commons@wikimedia.org">permissions-commons@wikimedia.org</a>')
    .replace(/\{vrt_template\}/g,
      '<a href="https://commons.wikimedia.org/wiki/Commons:Wikimedia_VRT_release_generator" ' +
      'target="_blank" rel="noopener noreferrer">' + t('provenance.vrt_template_text') + '</a>')
}
</script>

<style scoped>
.c2c-prov {
  border: 1px solid #c8ccd1;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 0;
  background: #fff;
}
.c2c-prov legend {
  font-weight: 600;
  font-size: 14px;
  padding: 0 4px;
  color: #202122;
}
.c2c-required { color: #d73333; }

.c2c-prov-intro { font-size: 12px; color: #54595d; margin: 4px 0 8px 0; line-height: 1.45; }

.c2c-prov-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 4px;
}
@media (max-width: 480px) { .c2c-prov-options { grid-template-columns: 1fr; } }

.c2c-prov-option {
  display: flex; align-items: center; gap: 12px;
  border: 2px solid #c8ccd1; border-radius: 6px;
  padding: 12px 14px; cursor: pointer; background: #f8f9fa;
  text-align: start; font: inherit;
  transition: border-color 0.15s, background 0.15s;
  min-height: 56px; box-sizing: border-box;
}
.c2c-prov-option:hover { border-color: #3366cc; background: #f0f4ff; }
.c2c-prov-option--active { border-color: #3366cc; background: #eaf3ff; }
/* One ring, not two. A 2px blue border plus a 2px blue outline two pixels
   outside it read as a double border; a soft halo reads as focus. The
   transparent outline is what keeps it visible under forced colours. */
.c2c-prov-option:focus-within {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}

.c2c-prov-radio-input { flex-shrink: 0; width: 18px; height: 18px; accent-color: #3366cc; cursor: pointer; }
.c2c-prov-text strong { font-size: 14px; font-weight: 600; color: #202122; line-height: 1.3; }

.c2c-prov-undo-hint { margin: 8px 0 0 0; font-size: 11px; color: #72777d; font-style: italic; }

.c2c-prov-branch {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #eaecf0;
  display: flex; flex-direction: column; gap: 12px;
}

/* The numbered questions used to be plain headings in a long form, so
   people scrolled straight past them. A tinted band with an accent edge
   makes each one an obvious stop without changing any wording. */
.c2c-prov-q {
  margin: 16px 0 8px 0;
  font-size: 14px; font-weight: 700; color: #202122;
  background: #eaf3ff;
  border-left: 4px solid var(--background-color-progressive, #36c);
  border-radius: 0 6px 6px 0;
  padding: 9px 12px;
}
.c2c-prov-q:first-of-type { margin-top: 8px; }
.c2c-prov-q-help { margin: -2px 0 8px 4px; font-size: 12px; color: #54595d; line-height: 1.4; }

.c2c-prov-radios { display: flex; flex-direction: column; gap: 6px; }
.c2c-prov-radio {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 8px 10px; border-radius: 4px; cursor: pointer;
  font-size: 13px; line-height: 1.45; min-height: 36px;
}
.c2c-prov-radio:hover { background: #f8f9fa; }
.c2c-prov-radio input[type="radio"] {
  flex-shrink: 0; width: 16px; height: 16px;
  margin-top: 2px; accent-color: #3366cc; cursor: pointer;
}
.c2c-prov-radio span { color: #202122; }
.c2c-prov-radio span a { color: #3366cc; }

.c2c-prov-sub {
  margin: 6px 0 6px 28px;
  padding: 12px 14px;
  background: #f8f9fa;
  border-left: 3px solid #c8ccd1;
  border-radius: 0 4px 4px 0;
  display: flex; flex-direction: column; gap: 8px;
}
.c2c-prov-sub-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
.c2c-prov-sub-field > span { font-size: 13px; font-weight: 600; color: #54595d; }
.c2c-prov-sub-field input,
.c2c-prov-sub-field textarea {
  width: 100%; box-sizing: border-box;
  padding: 10px 12px; font: inherit; font-size: 15px;
  border: 1px solid #a2a9b1; border-radius: 6px;
  background: #fff; color: #202122;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
.c2c-prov-sub-field textarea { resize: vertical; min-height: 68px; line-height: 1.45; }
.c2c-prov-sub-field input:focus,
.c2c-prov-sub-field textarea:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-prov-sub-note { margin: 0; font-size: 12px; color: #54595d; line-height: 1.45; }

.c2c-prov-sub h4 {
  margin: 0; font-size: 13px; font-weight: 600; color: #202122;
}

.c2c-prov-checks { display: flex; flex-direction: column; gap: 6px; }
.c2c-prov-check {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 6px 0; cursor: pointer; font-size: 13px;
  line-height: 1.45;
}
.c2c-prov-check input[type="checkbox"] {
  flex-shrink: 0; width: 16px; height: 16px;
  margin-top: 2px; accent-color: #3366cc; cursor: pointer;
}
.c2c-prov-check span { color: #202122; }

.c2c-prov-pd-eg, .c2c-prov-pd-note {
  margin: 0; font-size: 12px; color: #54595d; line-height: 1.45;
}

.c2c-prov-field { display: flex; flex-direction: column; gap: 3px; }
.c2c-prov-field label { font-weight: 600; font-size: 13px; color: #202122; }
.c2c-prov-field small { color: #54595d; font-size: 12px; }
.c2c-field-error { color: #b32424 !important; font-weight: 600; }
.c2c-prov-field input {
  padding: 10px 12px; border: 1px solid #a2a9b1; border-radius: 4px;
  font: inherit; font-size: 16px; box-sizing: border-box; width: 100%;
  min-height: 44px;
}
.c2c-prov-field input:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-prov-field input:disabled {
  background: #f0f0f0; color: #72777d; cursor: not-allowed;
}

.c2c-prov-block {
  background: #fee7e6; border: 1px solid #d73333; border-radius: 4px;
  padding: 10px 12px; margin: 6px 0 0 0;
  display: flex; gap: 8px; align-items: flex-start;
  color: #b32424; font-size: 13px; line-height: 1.45;
}
.c2c-prov-block :deep(svg) { flex-shrink: 0; margin-top: 1px; }

.c2c-prov-vrt {
  background: #fef6e7; border: 1px solid #f0d795; border-radius: 4px;
  padding: 10px 12px; margin: 0 0 6px 0;
  display: flex; gap: 8px; align-items: flex-start;
  color: #886600; font-size: 12px; line-height: 1.5;
}
.c2c-prov-vrt :deep(svg) { flex-shrink: 0; margin-top: 1px; }
.c2c-prov-vrt :deep(a) { color: #3366cc; }
</style>
